/**
 * Type-safe event emitter for the engine's model layer.
 *
 * <p>cs-core inherited Backbone's stringly-typed events; on a fresh codebase
 * we can use TypeScript discriminated unions for compile-time safety. Define
 * an event type as {@code { kind: 'something'; ... }} and pass the union as
 * {@code E} — {@code on('kind', listener)} narrows the listener's argument
 * to the matching variant.
 *
 * <p>Listeners are invoked synchronously in registration order. Throwing in
 * a listener is logged but doesn't abort other listeners — model events are
 * "fire and forget" notifications, not transactional.
 */
export type EventKind<E extends { kind: string }> = E['kind'];
export type EventOf<E extends { kind: string }, K extends EventKind<E>> = Extract<E, { kind: K }>;

export interface Disposable {
  dispose(): void;
}

export class EventEmitter<E extends { kind: string }> {
  private listeners = new Map<EventKind<E> | '*', Set<(event: E) => void>>();

  /**
   * Subscribe to a specific event kind. Returns a disposable that removes
   * the listener when {@code .dispose()} is called.
   */
  on<K extends EventKind<E>>(kind: K, listener: (event: EventOf<E, K>) => void): Disposable {
    const set = this.listeners.get(kind) ?? new Set();
    const erasedListener = listener as (event: E) => void;
    set.add(erasedListener);
    this.listeners.set(kind, set);
    return {
      dispose: () => {
        set.delete(erasedListener);
        if (set.size === 0) {
          this.listeners.delete(kind);
        }
      },
    };
  }

  /** Subscribe to every event regardless of kind — useful for debug overlays. */
  onAny(listener: (event: E) => void): Disposable {
    const set = this.listeners.get('*') ?? new Set();
    set.add(listener);
    this.listeners.set('*', set);
    return {
      dispose: () => {
        set.delete(listener);
        if (set.size === 0) {
          this.listeners.delete('*');
        }
      },
    };
  }

  /** Emit an event to all subscribers. Synchronous; listener order is registration order. */
  emit(event: E): void {
    const set = this.listeners.get(event.kind);
    if (set) {
      for (const listener of [...set]) {
        try {
          listener(event);
        } catch (err) {
          console.error('[EventEmitter] listener for', event.kind, 'threw:', err);
        }
      }
    }
    const wildcards = this.listeners.get('*');
    if (wildcards) {
      for (const listener of [...wildcards]) {
        try {
          listener(event);
        } catch (err) {
          console.error('[EventEmitter] wildcard listener threw on', event.kind, ':', err);
        }
      }
    }
  }

  /** Drop all listeners. Called when the host (model / scene) is destroyed. */
  clear(): void {
    this.listeners.clear();
  }
}
